import { Component } from '@theme/component';

/**
 * A custom element that renders animated sparkles within its parent container.
 * Uses GSAP for smooth fade/scale/rotation animations.
 * Reads configuration from data attributes set by the Liquid block.
 *
 * @extends Component
 */
export class SparklesComponent extends Component {
  #sparkles = [];
  #interval = null;
  #svgShapes = [];

  connectedCallback() {
    super.connectedCallback();

    // Read config from data attributes (set by Liquid block)
    this.config = {
      count: parseInt(this.dataset.count, 10) || 8,
      minSize: parseInt(this.dataset.minSize, 10) || 10,
      maxSize: parseInt(this.dataset.maxSize, 10) || 40,
      speed: parseFloat(this.dataset.speed) || 0.8,
      rotate: this.dataset.rotate !== 'false',
      color: this.dataset.color || '#ffffff',
    };

    // Collect inline SVG templates from the hidden container
    const svgContainer = this.querySelector('[data-sparkle-svgs]');
    if (svgContainer) {
      this.#svgShapes = Array.from(svgContainer.querySelectorAll('template')).map(
        (el) => el.innerHTML
      );
      svgContainer.remove();
    }

    if (this.#svgShapes.length === 0) return;

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #start() {
    // Spawn initial batch staggered over time
    for (let i = 0; i < this.config.count; i++) {
      setTimeout(() => this.#spawnSparkle(), i * (this.config.speed * 200));
    }
  }

  #stop() {
    if (this.#interval) clearInterval(this.#interval);
    this.#sparkles.forEach((el) => el.remove());
    this.#sparkles = [];
  }

  #spawnSparkle() {
    const sparkle = document.createElement('div');
    sparkle.classList.add('sparkle');
    sparkle.setAttribute('aria-hidden', 'true');

    // Random SVG shape
    const svgIndex = Math.floor(Math.random() * this.#svgShapes.length);
    sparkle.innerHTML = this.#svgShapes[svgIndex];

    // Random size between min and max
    const size = this.config.minSize + Math.random() * (this.config.maxSize - this.config.minSize);
    sparkle.style.width = `${size}px`;
    sparkle.style.height = `${size}px`;

    // Random position
    sparkle.style.left = `${Math.random() * 100}%`;
    sparkle.style.top = `${Math.random() * 100}%`;

    // Apply color
    sparkle.style.color = this.config.color;

    this.appendChild(sparkle);
    this.#sparkles.push(sparkle);

    this.#animateSparkle(sparkle);
  }

  #animateSparkle(sparkle) {
    if (typeof window.gsap === 'undefined') return;

    const speed = this.config.speed;
    const rotation = this.config.rotate ? Math.random() * 360 - 180 : 0;

    // Random delay so sparkles aren't all in sync
    const delay = Math.random() * speed * 2;

    window.gsap.fromTo(
      sparkle,
      {
        scale: 0,
        opacity: 0,
        rotation: 0,
      },
      {
        scale: 1,
        opacity: 0.8 + Math.random() * 0.2,
        rotation,
        duration: speed,
        delay,
        ease: 'power2.out',
        onComplete: () => {
          // Hold briefly, then fade out and respawn
          const holdTime = speed * 0.5 + Math.random() * speed;

          window.gsap.to(sparkle, {
            scale: 0,
            opacity: 0,
            rotation: rotation + (this.config.rotate ? 90 : 0),
            duration: speed * 0.8,
            delay: holdTime,
            ease: 'power2.in',
            onComplete: () => {
              // Reposition and respawn
              sparkle.style.left = `${Math.random() * 100}%`;
              sparkle.style.top = `${Math.random() * 100}%`;

              // Pick a new random SVG
              const svgIndex = Math.floor(Math.random() * this.#svgShapes.length);
              sparkle.innerHTML = this.#svgShapes[svgIndex];

              // New random size
              const size =
                this.config.minSize + Math.random() * (this.config.maxSize - this.config.minSize);
              sparkle.style.width = `${size}px`;
              sparkle.style.height = `${size}px`;

              this.#animateSparkle(sparkle);
            },
          });
        },
      }
    );
  }
}

if (!customElements.get('sparkles-component')) {
  customElements.define('sparkles-component', SparklesComponent);
}
